"use client";

import type { FC } from "react";
import { useEffect } from "react";
import { cn } from "utils";
import { FitnessRadarChart } from "@/src/components/fitness/RadarChart/FitnessRadarChart";
import { useRadarRevealAnimation } from "@/src/components/fitness/RadarChart/useRadarRevealAnimation";
import { mapFitnessScoreToRadarProps } from "@/src/hooks/onboarding/mapFitnessScoreToRadarProps";
import type { OnboardingV3FitnessScore } from "@/src/hooks/onboarding/onboardingV3Types";
import { trackEvent } from "@/src/utils/tracking";
import { FitnessDimensionScoreCard } from "../fitness/FitnessDimensionScoreCard";
import { FitnessScoreHeadline } from "../fitness/FitnessScoreHeadline";
import { FITNESS_SCORE_RESULT_COPY } from "../onboardingWizardCopy";
import { OnboardingEmphasisCtaButton } from "../shared/OnboardingEmphasisCtaButton";
import { OnboardingResponsiveCtaFooter } from "../shared/OnboardingResponsiveCtaFooter";
import { ONBOARDING_FLOATING_CTA_CONTENT_RESERVE_CLASS } from "../shared/onboardingFloatingCtaTokens";

export interface FitnessScoreResultStepProps {
  fitnessScore: OnboardingV3FitnessScore;
  onContinue: () => void;
}

export const FitnessScoreResultStep: FC<FitnessScoreResultStepProps> = ({
  fitnessScore,
  onContinue,
}) => {
  const radarProps = mapFitnessScoreToRadarProps(fitnessScore);
  const reveal = useRadarRevealAnimation({
    total: fitnessScore.total,
    control: fitnessScore.control,
    safety: fitnessScore.safety,
    growth: fitnessScore.growth,
    enabled: true,
  });

  useEffect(() => {
    trackEvent("ff_score_result_view", {
      total: fitnessScore.total,
      partial: fitnessScore.partial ?? false,
    });
  }, [fitnessScore.partial, fitnessScore.total]);

  const handleContinue = () => {
    trackEvent("ff_score_result_continue_tap", { total: fitnessScore.total });
    onContinue();
  };

  return (
    <div
      className="flex min-h-dvh flex-col bg-background_solid_neutral_secondary pt-safe max-md:h-dvh md:pt-0"
      data-testid="onboarding-fitness-score-result-step"
      data-figma-node="97267:68849"
      data-figma-node-pc="97283:76972"
    >
      <div
        className={cn(
          "flex min-h-0 flex-1 flex-col gap-8 overflow-y-auto no-scrollbar px-8 pt-8 md:mx-auto md:w-full md:max-w-[460px] md:px-8 md:pb-10",
          ONBOARDING_FLOATING_CTA_CONTENT_RESERVE_CLASS,
        )}
      >
        <section className="flex flex-col items-center gap-5 overflow-visible">
          <p className="body_medium_m w-full text-center text-content_primary">
            {FITNESS_SCORE_RESULT_COPY.radarTitle}
          </p>
          <FitnessRadarChart {...radarProps} size="onboarding-result" animate />
        </section>

        <section
          className="flex flex-col items-center gap-6"
          style={{ opacity: reveal.axisLabelOpacity }}
        >
          <FitnessScoreHeadline
            headline={fitnessScore.headline}
            scoreBasis={fitnessScore.scoreBasis}
          />
          <FitnessDimensionScoreCard
            control={fitnessScore.control}
            safety={fitnessScore.safety}
            growth={fitnessScore.growth}
            animate
            barWidths={reveal.barWidths}
            barLabelValues={reveal.barLabelValues}
          />
        </section>

        <div
          className="hidden w-full md:block"
          style={{ opacity: reveal.ctaOpacity }}
        >
          <OnboardingEmphasisCtaButton
            testId="fitness-score-result-cta-desktop"
            label={FITNESS_SCORE_RESULT_COPY.cta}
            sizeClassName="button_l"
            onClick={handleContinue}
          />
        </div>
      </div>

      <OnboardingResponsiveCtaFooter
        inset="narrow"
        testId="fitness-score-result-footer"
        className="md:hidden"
      >
        <div style={{ opacity: reveal.ctaOpacity }}>
          <OnboardingEmphasisCtaButton
            testId="fitness-score-result-cta"
            label={FITNESS_SCORE_RESULT_COPY.cta}
            sizeClassName="button_l"
            onClick={handleContinue}
          />
        </div>
      </OnboardingResponsiveCtaFooter>
    </div>
  );
};
